const getRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const getString = (value: unknown): string => (typeof value === 'string' ? value : '');

const getNumeric = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const collectErrorTexts = (input: unknown): string[] => {
    const root = getRecord(input);
    if (!root) {
        return [getString(input)];
    }

    const error = getRecord(root.error);
    const networkError = getRecord(root.networkError);
    const causes = Array.isArray(error?.causes) ? error.causes : [];

    return [
        getString(root.message),
        getString(root.stack),
        getString(error?.message),
        getString(error?.stack),
        getString(networkError?.message),
        getString(networkError?.stack),
        ...causes.flatMap((cause) => {
            const causeRecord = getRecord(cause);
            if (!causeRecord) {
                return [];
            }

            return [getString(causeRecord.message), getString(causeRecord.stack)];
        }),
    ];
};

const hasNetworkFailureSignature = (texts: string[]): boolean =>
    texts.some((text) => {
        const normalized = text.toLowerCase();
        return (
            normalized.includes('load failed') ||
            normalized.includes('failed to fetch') ||
            normalized.includes('networkerror') ||
            normalized.includes('aborterror')
        );
    });

const hasExternalObjectNotFoundSignature = (texts: string[]): boolean =>
    texts.some((text) => {
        const normalized = text.toLowerCase();
        return (
            normalized.includes('object not found matching id') ||
            (normalized.includes('methodname:update') && normalized.includes('paramcount:4'))
        );
    });

export const isSilencedOidcRuntimeError = (error: unknown, pathname?: string): boolean => {
    const normalized = collectErrorTexts(error).map((text) => text.toLowerCase());
    const currentPath = (pathname || '').toLowerCase();

    const isAuthCallbackPath = currentPath.includes('/auth/callback') || currentPath.includes('/auth/silent-callback');
    const hasOidcMarker = normalized.some(
        (text) =>
            text.includes('signinredirect') ||
            text.includes('signinsilent') ||
            text.includes('signincallback') ||
            text.includes('errorresponse') ||
            text.includes('invalid_grant'),
    );
    const hasNetworkFailure = hasNetworkFailureSignature(normalized);

    return (isAuthCallbackPath && hasNetworkFailure) || (hasOidcMarker && hasNetworkFailure);
};

export const isSilencedOidcDatadogEvent = (event: unknown): boolean => {
    const root = getRecord(event);
    if (!root) {
        return false;
    }

    const http = getRecord(root.http);
    const resource = getRecord(root.resource);
    const method = getString(http?.method).toLowerCase();
    const httpUrl = getString(http?.url).toLowerCase();
    const resourceUrl = getString(resource?.url).toLowerCase();
    const normalized = [...collectErrorTexts(event), httpUrl, resourceUrl].map((text) => text.toLowerCase());

    const isOidcTokenExchange =
        normalized.some((text) => text.includes('/oauth2/token')) ||
        normalized.some((text) => text.includes('signinsilent'));
    const isPost = method === 'post' || normalized.some((text) => text.includes('fetch error post'));
    const isStatusZero = getNumeric(http?.status_code) === 0 || getNumeric(resource?.status_code) === 0;

    return isOidcTokenExchange && isPost && isStatusZero && hasNetworkFailureSignature(normalized);
};

export const isSilencedNextRscConsoleError = (args: unknown[]): boolean => {
    const normalized = args.flatMap((arg) => collectErrorTexts(arg)).map((text) => text.toLowerCase());

    const hasRscPayloadFailure = normalized.some((text) => text.includes('failed to fetch rsc payload'));
    const hasFallbackNavigation = normalized.some((text) => text.includes('falling back to browser navigation'));
    const hasFailedToFetch = normalized.some((text) => text.includes('failed to fetch'));
    const hasRscQuery = normalized.some((text) => text.includes('_rsc='));

    return (hasRscPayloadFailure && hasFallbackNavigation) || (hasFailedToFetch && hasRscQuery);
};

export const isSilencedExternalRuntimeNoiseError = (error: unknown): boolean =>
    hasExternalObjectNotFoundSignature(collectErrorTexts(error).map((text) => text.toLowerCase()));

export const isSilencedExternalRuntimeNoiseConsoleError = (args: unknown[]): boolean =>
    hasExternalObjectNotFoundSignature(args.flatMap((arg) => collectErrorTexts(arg)).map((text) => text.toLowerCase()));
